import { useEffect, useState } from 'react';
import { ChevronsUp } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * BACK TO TOP
 *
 * Some screens here are genuinely long — a course list, the audit log, a
 * department roster of sixty — and the things you go back up for are the ones
 * that matter: the search box, the tabs, the page's own actions.
 *
 * It listens to the WINDOW, which is what actually scrolls: the shell is
 * `min-h-screen` with a sticky sidebar, so the page grows and the document
 * scrolls. (The sidebar's own nav scrolls separately and is deliberately not
 * what this watches — jumping the page because somebody scrolled the menu
 * would be baffling.)
 * ═══════════════════════════════════════════════════════════════════════════
 */

/**
 * How far down the button appears.
 *
 * Three quarters of a screen, so it shows up on a page long enough for the
 * trip back to be a real one and stays out of the way on a page that barely
 * scrolls. A fixed pixel figure was the first attempt and is wrong on both
 * ends — 400px is most of a laptop screen and a third of a monitor.
 *
 * Exported for its test: the threshold is the whole of the behaviour, and it
 * is the part that cannot be checked by looking at the button.
 */
export function shouldOfferReturn(scrollY: number, viewportHeight: number): boolean {
  return scrollY > viewportHeight * 0.75;
}

export function BackToTop() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    /**
     * Read inside an animation frame, and never more than one at a time.
     *
     * Scroll fires far more often than the screen redraws, and reading
     * `scrollY` forces the browser to settle the layout — doing that per
     * event is how a scroll listener turns into jank on the long pages this
     * exists for.
     */
    let frame = 0;

    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        setVisible(shouldOfferReturn(window.scrollY, window.innerHeight));
      });
    };

    onScroll(); // A page restored mid-scroll should show it immediately.
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  const toTop = () => {
    /**
     * Smooth, unless the reader has asked for less motion — for whom a long
     * animated scroll is not a flourish but a symptom trigger. The media
     * query is read at click time rather than cached, because the setting can
     * change while the tab is open.
     */
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
  };

  return (
    <button
      type="button"
      onClick={toTop}
      /*
        Hidden from assistive tech and from the keyboard when it is not
        offered. Fading it out alone would leave an invisible button in the
        tab order — somebody tabbing through the page would land on a control
        they cannot see and cannot explain.
      */
      aria-hidden={!visible}
      tabIndex={visible ? 0 : -1}
      aria-label="Back to top"
      title="Back to top"
      className={cn(
        /*
          44px, which is the smallest thing a thumb reliably hits and the
          floor every touch-target guideline settles on. It was 40 and read as
          slightly undersized against the cards it floats over.
        */
        'fixed bottom-5 right-5 z-40 flex h-11 w-11 items-center justify-center rounded-full',
        /*
          The product's own green, from the token rather than a hex — the same
          value as a primary button and the selected sidebar item, so it reads
          as part of the interface rather than pasted onto it. White on this
          green is the highest-contrast pair the palette has.
        */
        'bg-primary text-primary-foreground shadow-lg',
        'transition-all duration-200 hover:bg-primary/90 hover:shadow-xl',
        /* Nothing under it is blocked while it is hidden. */
        visible
          ? 'pointer-events-auto translate-y-0 opacity-100'
          : 'pointer-events-none translate-y-2 opacity-0',
        /* The translate is decoration; honour a request for less of it. */
        'motion-reduce:transition-none motion-reduce:translate-y-0',
      )}
    >
      {/*
        A double chevron, not a single arrow: two strokes say "all the way
        back", where one says "up a bit" — and this control only ever goes to
        the very top.

        20px inside 44px leaves the glyph about 45% of the circle, which is
        what stops it reading either as a sticker on a button or as a button
        that is mostly padding. `strokeWidth` is above the 1.75 used elsewhere
        because white on a dark ground needs the extra weight to hold its
        shape at this size.
      */}
      <ChevronsUp className="h-5 w-5" strokeWidth={2.5} aria-hidden />
    </button>
  );
}
